import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { vehiclesApi } from '@/api/endpoints'
import type { TravelRatesTable, Vehicle } from '@/api/types'
import { Card } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { FUEL_LABELS, OWNERSHIP_LABELS, formatDate, formatNumber, formatPlate } from '@/utils/format'

/** Vozový park: seznam aut a zákonné sazby, ze kterých se počítají náhrady. */
export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null)
  const [rates, setRates] = useState<TravelRatesTable | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    vehiclesApi
      .list()
      .then(setVehicles)
      .catch((e) => setError(errorMessage(e, 'Auta se nepodařilo načíst.')))
    vehiclesApi.rates().then(setRates).catch(() => setRates(null))
  }, [])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Vozový park</h1>
        <Link
          to="/vozidla/nove"
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Přidat auto
        </Link>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!vehicles && !error && <p className="text-slate-400">Načítání…</p>}

      {vehicles && vehicles.length === 0 && (
        <Card className="text-center text-sm text-slate-500">
          Zatím tu není žádné auto. Přidej první a nahraj k němu výpis z technického průkazu.
        </Card>
      )}

      {vehicles && vehicles.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">SPZ</th>
                <th className="px-4 py-3">Auto</th>
                <th className="px-4 py-3">Vlastník</th>
                <th className="px-4 py-3">Palivo</th>
                <th className="px-4 py-3 text-right">Spotřeba / 100 km</th>
                <th className="px-4 py-3 text-right">Náhrada / km dnes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vehicles.map((v) => (
                <tr key={v.id} className={`hover:bg-slate-50 ${v.active ? '' : 'text-slate-400'}`}>
                  <td className="whitespace-nowrap px-4 py-3 font-mono">
                    <Link to={`/vozidla/${v.id}`} className="font-medium text-brand-700 hover:underline">
                      {formatPlate(v.plate)}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    {v.name}
                    {!v.active && <span className="ml-2 text-xs">(vyřazené)</span>}
                    {!v.hasTechnicalCertificate && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">chybí TP</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {v.ownership === 'EMPLOYEE' ? v.ownerName : OWNERSHIP_LABELS.COMPANY}
                  </td>
                  <td className="px-4 py-3">{FUEL_LABELS[v.fuelType]}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    {v.rateToday.consumption !== null ? `${formatNumber(v.rateToday.consumption)} ${v.fuelUnit}` : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium">
                    {v.rateToday.total !== null ? `${formatNumber(v.rateToday.total)} Kč` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {rates && <RatesCard rates={rates} />}
    </div>
  )
}

const FUEL_ORDER = Object.keys(FUEL_LABELS)

/** Sazby z vyhlášek MPSV, nejnovější nahoře; dnes platné zvýrazněné. */
function RatesCard({ rates }: { rates: TravelRatesTable }) {
  const today = new Date().toISOString().slice(0, 10)
  const current = <T extends { validFrom: string }>(rows: T[], key: (r: T) => string) => {
    const best = new Map<string, string>()
    rows.filter((r) => r.validFrom <= today).forEach((r) => best.set(key(r), r.validFrom))
    return (r: T) => best.get(key(r)) === r.validFrom
  }
  const isCurrentKm = current(rates.kmRates, (r) => r.category)
  const isCurrentFuel = current(rates.fuelPrices, (r) => r.fuelType)

  return (
    <Card>
      <h2 className="mb-1 font-semibold">Zákonné sazby cestovních náhrad</h2>
      <p className="mb-4 text-sm text-slate-500">
        Mění se vyhláškou i během roku — jízda se počítá sazbou platnou v den jízdy. Firemní auto: jen PHM, pokud ho
        zaměstnanec zaplatil. Soukromé auto: základní náhrada (amortizace) + PHM.
      </p>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Základní náhrada za 1 km</h3>
          <ul className="space-y-1 text-sm">
            {[...rates.kmRates]
              .sort((a, b) => a.category.localeCompare(b.category) || b.validFrom.localeCompare(a.validFrom))
              .map((r) => (
              <li key={r.category + r.validFrom} className={isCurrentKm(r) ? '' : 'text-slate-400'}>
                {r.category === 'CAR' ? 'Osobní auto' : 'Motocykl'}: <strong>{formatNumber(r.rate)} Kč</strong> od{' '}
                {formatDate(r.validFrom)} <span className="text-xs text-slate-400">({r.source})</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Průměrné ceny PHM</h3>
          <ul className="space-y-1 text-sm">
            {[...rates.fuelPrices]
              .sort((a, b) => FUEL_ORDER.indexOf(a.fuelType) - FUEL_ORDER.indexOf(b.fuelType) || b.validFrom.localeCompare(a.validFrom))
              .map((r) => (
                <li key={r.fuelType + r.validFrom} className={isCurrentFuel(r) ? '' : 'text-slate-400'}>
                  {FUEL_LABELS[r.fuelType]}: <strong>{formatNumber(r.price)} Kč/{r.unit}</strong> od{' '}
                  {formatDate(r.validFrom)} <span className="text-xs text-slate-400">({r.source})</span>
                </li>
              ))}
          </ul>
          <p className="mt-2 text-xs text-slate-400">LPG a CNG průměrnou cenu nemají — cena se bere z dokladu.</p>
        </div>
      </div>
    </Card>
  )
}
